import { branding } from '@guridosdrones/shared';
import { Card } from '@guridosdrones/ui';
import { motion } from 'framer-motion';
import { ArrowRight, Briefcase, GraduationCap, Radar } from 'lucide-react';
import { FadeIn } from '@/components';

export function OtherCourses() {
  const courses = [
    {
      title: 'Monitoramento e Mapeamento de Talhões',
      description:
        'Use leitura de área e inteligência de campo para montar operações mais previsíveis e rentáveis.',
      icon: Radar,
      code: 'EXT_01',
      tags: ['Especialização', 'Online'],
      features: ['Leitura de Talhão', 'Planejamento de Rotas', 'Relatórios de Campo'],
    },
    {
      title: 'Gestão de Negócios',
      description:
        'Transforme sua habilidade de pilotagem em uma empresa lucrativa. Aprenda precificação, vendas e gestão de frota.',
      icon: Briefcase,
      code: 'EXT_02',
      tags: ['Business', 'Online'],
      features: ['Planilhas de Custo', 'Modelos de Contrato', 'Estratégias de Venda'],
    },
    {
      title: 'Mentoria Individual',
      description:
        'Acelere seus resultados com um acompanhamento personalizado direto com o Guri. Tire dúvidas específicas e trace seu plano de carreira.',
      icon: GraduationCap,
      code: 'EXT_03',
      tags: ['VIP', 'Ao Vivo'],
      features: ['Calls Semanais', 'Análise de Negócio', 'Networking Exclusivo'],
    },
  ];

  return (
    <section className="section-shell bg-muted/30 transition-colors duration-300">
      {/* Decorative Lines */}
      <div className="absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-border to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-linear-to-r from-transparent via-border to-transparent" />

      <div className="section-container">
        <div className="section-header">
          <FadeIn direction="up" distance={20}>
            <p className="mb-3 font-mono text-xs tracking-[0.25em] text-primary/70 uppercase">
              Missões Extras
            </p>
            <h2 className="section-title mb-4">Especializações Para Crescer</h2>
            <p className="section-description">
              Depois de dominar a pilotagem, escale seus resultados. Mapeamento, gestão e mentoria
              para quem quer faturar mais no agro.
            </p>
          </FadeIn>
        </div>

        <div className="mobile-snap-row md:mx-0 md:grid md:grid-cols-2 md:gap-8 md:overflow-visible md:px-0 lg:grid-cols-3">
          {courses.map((course, index) => (
            <motion.div
              key={course.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{ y: -5 }}
              className="mobile-snap-item min-w-[84vw] sm:min-w-[72vw] md:min-w-0"
            >
              <Card className="group relative flex h-full flex-col overflow-hidden border-border bg-card p-7 shadow-sm transition-all duration-300 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/10">
                {/* Hover gradient */}
                <div className="absolute inset-0 bg-linear-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                <div className="relative z-10 flex flex-col h-full">
                  <div className="mb-6 sm:hidden">
                    <div className="relative min-h-16">
                      <div className="w-fit self-start border border-primary/15 bg-primary/8 p-4 text-primary transition-transform duration-300 group-hover:scale-110">
                        <course.icon className="w-8 h-8" />
                      </div>
                      <div className="absolute top-0 right-0 flex max-w-[calc(100%-5.5rem)] flex-col items-end gap-1.5">
                        {course.tags.map((tag) => (
                          <span
                            key={tag}
                            className="w-fit border border-border bg-muted px-2.5 py-1 font-mono text-[8px] font-bold tracking-[0.12em] text-muted-foreground uppercase"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mb-6 hidden items-start justify-between gap-3 sm:flex">
                    <div className="w-fit self-start border border-primary/15 bg-primary/8 p-4 text-primary transition-transform duration-300 group-hover:scale-110">
                      <course.icon className="w-8 h-8" />
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex gap-2">
                        {course.tags.map((tag) => (
                          <span
                            key={tag}
                            className="w-fit border border-border bg-muted px-2.5 py-1 font-mono text-[9px] font-bold tracking-[0.12em] text-muted-foreground uppercase"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <span className="font-mono text-[9px] tracking-widest text-muted-foreground/40">
                        {course.code}
                      </span>
                    </div>
                  </div>

                  <h3 className="mb-3 text-2xl font-bold text-card-foreground transition-colors group-hover:text-primary">
                    {course.title}
                  </h3>

                  <p className="mb-8 leading-relaxed text-muted-foreground">{course.description}</p>

                  <div className="mb-8 flex grow flex-col gap-3">
                    {course.features.map((feature, i) => (
                      <div
                        key={feature}
                        className="flex items-center gap-3 text-sm text-foreground/80"
                      >
                        <span className="font-mono text-[9px] tracking-widest text-primary/40">
                          0{i + 1}
                        </span>
                        <span className="h-px w-3 bg-primary/20" />
                        {feature}
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    className="group/btn w-full inline-flex items-center justify-center gap-2 border border-border bg-transparent py-3 font-mono text-xs font-bold tracking-widest text-foreground uppercase transition-all hover:border-primary/50 hover:bg-primary/8 hover:text-primary"
                    onClick={() => window.open(branding.contact.whatsapp, '_blank')}
                  >
                    Saiba Mais
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/btn:translate-x-1" />
                  </button>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
